import React, { useMemo } from 'react';

import { Box } from '@mui/material';
import IllustratedMessage from '@apigrowfinance/pbolt-illustrated-message';
import { RiErrorWarningLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import DelinquencyHistory from '@components/delinquency/DelinquencyHistory';
import { useAccount } from '@contexts/AccountProvider';
import { ProgramParameters } from '@enums/programs/programParameters';
import useGetDelinquency from '@hooks/queries/useGetDelinquency';
import useLoadMoreIfNecessary from '@hooks/useLoadMoreIfNecessary';
import Graph from '@pages/delinquency/graph/Graph';

const Delinquency = () => {
    const { accountId } = useParams();
    const {
        programHasParameter,
        isLoading: isLoadingAccount,
        error: errorAccount,
        program,
    } = useAccount();

    const isDelinquencyEnabled = useMemo(
        () => programHasParameter(ProgramParameters.DELINQUENCY),
        [programHasParameter],
    );

    const {
        data: delinquency,
        isLoading: isLoadingDelinquency,
        isError: isErrorDelinquency,
        fetchNextPage,
        hasNextPage,
        isSuccess,
    } = useGetDelinquency({
        accountId: +accountId,
        enabled: isDelinquencyEnabled,
    });

    const isLoading = isLoadingDelinquency || isLoadingAccount;

    useLoadMoreIfNecessary(
        {
            hasNextPage,
            isLoading,
            fetchNextPage,
        },
        [delinquency],
    );

    // We only want to show the last 12 cycles.
    // We could end with more when the cycle history pages
    const lasted12Cycles = delinquency?.slice(0, 12);

    const noDelinquencyData = isSuccess && delinquency?.length === 0;

    if (noDelinquencyData) {
        return (
            <Box sx={{ mt: 10 }}>
                <IllustratedMessage
                    icon={<RiErrorWarningLine />}
                    messageMain={
                        <FormattedMessage
                            id="Delinquency.noCyclesTitle"
                            defaultMessage="No delinquency"
                        />
                    }
                    messageSub={
                        <FormattedMessage
                            id="Delinquency.noCyclesSubTitle"
                            defaultMessage="There are no cycles to show"
                        />
                    }
                />
            </Box>
        );
    }

    return (
        <div>
            <Box sx={{ mt: 6 }}>
                <Graph
                    delinquency={lasted12Cycles}
                    isLoading={isLoading}
                    isError={
                        isErrorDelinquency ||
                        errorAccount?.accountIsError ||
                        errorAccount?.programIsError
                    }
                    currency={program?.currency_numeric_code}
                />
            </Box>
            <Box sx={{ mt: 5 }}>
                <DelinquencyHistory
                    fetchNextPage={fetchNextPage}
                    hasNextPage={hasNextPage}
                    delinquencyBuckets={delinquency}
                    isLoading={isLoading}
                />
            </Box>
        </div>
    );
};

export default Delinquency;
